import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCanEdit } from '@/features/auth/AuthContext';
import {
  useCreateObra,
  useDeleteObra,
  useMergeObra,
  useObras,
  useUpdateObra,
} from '@/features/obras/hooks';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { inputClass } from '@/components/ui/Field';

interface CatalogueItem {
  id: string;
  nome: string;
}

interface CatalogueCardProps {
  title: string;
  placeholder: string;
  items: CatalogueItem[];
  canEdit: boolean;
  onAdd: (nome: string) => void;
  onRename: (item: CatalogueItem, nome: string) => void;
  onRemove: (item: CatalogueItem) => void;
  confirmRemove: string;
  onMerge?: (source: CatalogueItem, targetId: string) => void;
}

function CatalogueCard({
  title,
  placeholder,
  items,
  canEdit,
  onAdd,
  onRename,
  onRemove,
  confirmRemove,
  onMerge,
}: CatalogueCardProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [mergingId, setMergingId] = useState<string | null>(null);

  const add = () => {
    const trimmed = value.trim();
    if (trimmed) onAdd(trimmed);
    setValue('');
  };

  const startEdit = (item: CatalogueItem) => {
    setEditingId(item.id);
    setEditValue(item.nome);
  };

  const saveEdit = (item: CatalogueItem) => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== item.nome) onRename(item, trimmed);
    setEditingId(null);
  };

  return (
    <Card className="flex flex-col gap-2.5 p-4">
      <h2 className="text-[17px] font-bold">{title}</h2>
      {items.map((item) => {
        const editing = editingId === item.id;
        const merging = mergingId === item.id;
        const targets = items.filter((m) => m.id !== item.id);
        return (
          <div
            key={item.id}
            className="flex items-center justify-between gap-2 border-b border-[#e9ecf7] py-2 text-sm"
          >
            {editing ? (
              <>
                <input
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveEdit(item);
                    if (e.key === 'Escape') setEditingId(null);
                  }}
                  autoFocus
                  className={`${inputClass} flex-1`}
                />
                <button
                  type="button"
                  aria-label={t('common.save')}
                  onClick={() => saveEdit(item)}
                  className="h-8 w-8 rounded-lg bg-conduril text-white"
                >
                  ✓
                </button>
                <button
                  type="button"
                  aria-label={t('common.cancel')}
                  onClick={() => setEditingId(null)}
                  className="h-8 w-8 rounded-lg bg-conduril-tint"
                >
                  ✕
                </button>
              </>
            ) : merging ? (
              <>
                <select
                  autoFocus
                  defaultValue=""
                  onChange={(e) => {
                    const targetId = e.target.value;
                    if (!targetId || !onMerge) return;
                    const target = targets.find((x) => x.id === targetId);
                    if (window.confirm(t('obras.confirmMerge', { source: item.nome, target: target?.nome })))
                      onMerge(item, targetId);
                    setMergingId(null);
                  }}
                  className={`${inputClass} flex-1`}
                >
                  <option value="">{t('obras.mergeSelect')}</option>
                  {targets.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.nome}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label={t('common.cancel')}
                  onClick={() => setMergingId(null)}
                  className="h-8 w-8 rounded-lg bg-conduril-tint"
                >
                  ✕
                </button>
              </>
            ) : (
              <>
                <span className="flex-1">{item.nome}</span>
                {canEdit && (
                  <button
                    type="button"
                    aria-label={t('common.edit')}
                    onClick={() => startEdit(item)}
                    className="h-8 w-8 rounded-lg bg-conduril-tint"
                  >
                    ✎
                  </button>
                )}
                {canEdit && onMerge && targets.length > 0 && (
                  <button
                    type="button"
                    title={t('obras.merge')}
                    aria-label={t('obras.merge')}
                    onClick={() => setMergingId(item.id)}
                    className="h-8 w-8 rounded-lg bg-conduril-tint"
                  >
                    ⇄
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    aria-label={t('common.remove')}
                    onClick={() => window.confirm(confirmRemove) && onRemove(item)}
                    className="h-8 w-8 rounded-lg bg-danger-bg text-danger-text"
                  >
                    ✕
                  </button>
                )}
              </>
            )}
          </div>
        );
      })}
      {canEdit && (
        <div className="flex gap-2">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder={placeholder}
            className={`${inputClass} flex-1`}
          />
          <button
            type="button"
            onClick={add}
            className="min-h-[40px] rounded-lg bg-conduril px-3.5 font-bold text-white hover:bg-conduril-dark"
          >
            ＋
          </button>
        </div>
      )}
    </Card>
  );
}

export function ObrasPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canEdit = useCanEdit();

  const obras = useObras();
  const createObra = useCreateObra();
  const updateObra = useUpdateObra();
  const deleteObra = useDeleteObra();
  const mergeObra = useMergeObra();

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <h1 className="text-xl font-bold text-conduril-ink">{t('nav.obras')}</h1>
      <CatalogueCard
        title={t('obras.currentTitle')}
        placeholder={t('obras.addObra')}
        items={obras.data ?? []}
        canEdit={canEdit}
        onAdd={(nome) => createObra.mutate(nome)}
        onRename={(item, nome) => updateObra.mutate({ id: item.id, nome })}
        onMerge={(item, targetId) => mergeObra.mutate({ sourceId: item.id, targetId })}
        onRemove={(item) => deleteObra.mutate(item.id)}
        confirmRemove={t('obras.confirmRemoveObra')}
      />

      <Button variant="secondary" onClick={() => navigate('/')}>
        ← {t('common.back')}
      </Button>
    </div>
  );
}
